// src/services/authService.ts
import api from './api';
import { 
  LoginCredentials, 
  RegisterCredentials, 
  SocialAuthCredentials 
} from '../types/auth';
import { 
  ApiError 
} from '../types/auth';

import { RegisterFormData } from '../types/register';
import { AuthResponse } from '../types/auth';

class AuthService {
  private static instance: AuthService;
  private readonly tokenKey = 'auth_token';
  
  private constructor() {}
  
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }
  

  async socialAuth(credentials: SocialAuthCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>(
        `/auth/${credentials.provider}`,
        {
          code: credentials.code,
          state: credentials.state
        }
      );
      
      if (response.data.token) {
        this.setToken(response.data.token);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }


  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);
      if (response.data.token) {
        this.setToken(response.data.token);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/register', {
        ...credentials,
        role: credentials.role || undefined,
        experience: credentials.experience || undefined
      });
      
      if (response.data.token) {
        this.setToken(response.data.token);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  async getCurrentUser(): Promise<AuthResponse['user']> {
    try {
      const response = await api.get<AuthResponse['user']>('/auth/me');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    const apiError = error as ApiError;
    if (apiError.response?.data?.message) {
      return new Error(apiError.response.data.message);
    }
    if (apiError.response?.data?.errors?.length) {
      return new Error(apiError.response.data.errors[0]);
    }
    return new Error('An unexpected error occurred');
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
  }

  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }


  async updateProfile(updates: Partial<RegisterCredentials>) {
    const { data } = await api.put('/auth/profile', updates);
    return data;
  }
}

export const authService = AuthService.getInstance();